import { useCallback, useEffect, useRef, useState } from 'react'
import { StatusCodes } from 'http-status-codes'
import { toCreatePayload } from '@/features/consignment-form/api-payload'
import { confirmationSchema, createRejectionSchema } from '@/features/consignment-form/contract'
import { clearAttempt, clearDraft, hasSavedAttempt, readAttempt, writeAttempt } from '@/features/consignment-form/draft'
import type { CreateAttempt } from '@/features/consignment-form/draft'
import { formatBackendError } from '@/features/consignment-form/labels'
import type { Product } from '@/features/consignment-form/products'
import type { Consignment } from '@/features/consignment-form/schema'
import { t } from '@/lang'
import { postJson } from '@/lib/api/browser'
import { localApi } from '@/lib/api/endpoints'

type Status = 'idle' | 'submitting' | 'unknown' | 'blocked' | 'success'

const storageAdvice = t('submission.storage-advice')

const messages = {
  unreadableAttempt: t('submission.unreadable-attempt'),
  storageUnavailable: `${t('submission.storage-unavailable')} ${storageAdvice}`,
  storageWriteFailed: `${t('submission.storage-write-failed')} ${storageAdvice}`,
}

/** What a saved create attempt forces on the form before anything new is sent. */
const savedAttemptHold = (): { status?: 'unknown' | 'blocked'; message?: string } | undefined => {
  if (readAttempt()) return { status: 'unknown' }
  const presence = hasSavedAttempt()
  if (presence === true) return { status: 'blocked', message: messages.unreadableAttempt }
  if (presence === undefined) return { message: messages.storageUnavailable }
  return undefined
}

/**
 * The create request and its outcome. The attempt is stored before it is sent, so an unknown
 * result can be retried with the same key and body, even after a reload.
 */
export function useSubmission({ onCreated }: { onCreated: () => void }) {
  const [status, setStatus] = useState<Status>('idle')
  const [message, setMessage] = useState('')
  const [details, setDetails] = useState<{ headline: string; lines: string[] }>()
  const [createdNumber, setCreatedNumber] = useState('')
  // Bumped when a Submit is answered with a message, so the form can bring that message into view.
  const [notice, setNotice] = useState(0)
  const inFlight = useRef(false)

  /** Applies the hold a saved attempt puts on the form; true when nothing new may be sent. */
  const held = useCallback(() => {
    const hold = savedAttemptHold()
    if (hold?.status) setStatus(hold.status)
    if (hold?.message) setMessage(hold.message)
    return hold !== undefined
  }, [])

  /* oxlint-disable react/set-state-in-effect -- A stored attempt is browser-only state, read after hydration. */
  useEffect(() => {
    held()
  }, [held])
  /* oxlint-enable react/set-state-in-effect */

  const say = (text: string, backend?: { headline: string; lines: string[] }) => {
    setMessage(text)
    setDetails(backend)
    setNotice((count) => count + 1)
  }

  const send = async (attempt: CreateAttempt) => {
    if (inFlight.current) return
    inFlight.current = true
    setStatus('submitting')
    setMessage('')
    setDetails(undefined)
    try {
      const response = await postJson(localApi.consignments, attempt.body, { 'Idempotency-Key': attempt.key })
      // A rejection is final on a retry too: f-edge answers a key it has booked with that booking, before any check.
      if (response.status === StatusCodes.BAD_REQUEST) {
        clearAttempt()
        setStatus('idle')
        // The backend's own words replace ours when it sent any.
        const rejection = createRejectionSchema.safeParse(await response.json().catch(() => undefined))
        const backend = rejection.success ? rejection.data.backend : undefined
        if (backend) say(t('submission.not-created'), formatBackendError(backend))
        else say(t('submission.not-created-check'))
        return
      }
      if (!response.ok) throw new Error('Create result unknown')
      const confirmation = confirmationSchema.safeParse(await response.json())
      if (!confirmation.success) throw new Error('Create result unknown')
      clearAttempt()
      clearDraft()
      onCreated()
      setCreatedNumber(confirmation.data.consignmentNumber)
      setStatus('success')
    } catch {
      setStatus('unknown')
    } finally {
      inFlight.current = false
    }
  }

  const submit = (values: Consignment, products: readonly Product[]) => {
    if (inFlight.current) return
    if (held()) {
      setNotice((count) => count + 1)
      return
    }
    try {
      const body = JSON.stringify(toCreatePayload(values, products))
      const attempt = { key: crypto.randomUUID(), body }
      if (!writeAttempt(attempt)) {
        say(messages.storageWriteFailed)
        return
      }
      void send(attempt)
    } catch {
      setMessage(t('submission.check-form'))
    }
  }

  const retry = () => {
    const attempt = readAttempt()
    if (attempt) void send(attempt)
  }

  /** A message that is not an answer to Submit, so it does not take focus. */
  const warn = (text: string) => {
    setMessage(text)
    setDetails(undefined)
  }

  return { status, message, details, notice, createdNumber, submit, retry, warn }
}
